import { getBankIntegrationPageURL } from '@constants'
import { useSessionStorage } from '@hooks'
import { BusinessOnboardingStepKey } from '@query'
import { useCallback } from 'react'
import { useHistory, useLocation } from 'react-router'

const BANK_STEP = BusinessOnboardingStepKey.BANK_INTEGRATION

export type GetStartedBankReturnKind = 'enableBanking' | 'holvi'

export const getStartedPendingStepKey = (slug: string) => `getStartedWidget.${slug}.pendingStep`
export const getStartedSawUnfinishedKey = (slug: string) => `getStartedWidget.${slug}.sawUnfinished`
export const getStartedBankReturnKindKey = (slug: string) =>
  `getStartedWidget.${slug}.bankReturnKind`

const writeSessionJson = (key: string, value: unknown): void => {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Storage can be unavailable; callers treat a missing flag as "no handover".
  }
}

const rememberGetStartedBankHandoverPending = (slug: string): void => {
  writeSessionJson(getStartedPendingStepKey(slug), BANK_STEP)
}

export const rememberGetStartedBankReturnKind = (
  slug: string,
  kind: GetStartedBankReturnKind
): void => {
  writeSessionJson(getStartedBankReturnKindKey(slug), kind)
}

const readSessionJson = (key: string): unknown => {
  try {
    const raw = window.sessionStorage.getItem(key)
    return raw === null ? null : JSON.parse(raw)
  } catch {
    return null
  }
}

export const shouldLetGetStartedHandleBankReturn = (slug: string): boolean =>
  readSessionJson(getStartedPendingStepKey(slug)) === BANK_STEP

export const clearGetStartedBankHandoverPending = (slug: string): void => {
  window.sessionStorage.removeItem(getStartedPendingStepKey(slug))
}

export const useBankHandover = (slug: string) => {
  const history = useHistory()
  const location = useLocation()
  const [pendingStep, setPendingStep] = useSessionStorage<string | null>(
    getStartedPendingStepKey(slug),
    null
  )
  const [returnKind, setReturnKind] = useSessionStorage<GetStartedBankReturnKind | null>(
    getStartedBankReturnKindKey(slug),
    null
  )

  const isOnBankFlow = location.pathname.includes('/bank-integration-flow/')
  const justReturned = pendingStep === BANK_STEP && !isOnBankFlow

  const startBankHandover = useCallback(() => {
    rememberGetStartedBankHandoverPending(slug)
    setPendingStep(BANK_STEP)
    // Bank flow is outside <Page>, so navigating unmounts this widget. Wait a
    // tick so the pending flag is committed before that unmount.
    window.setTimeout(() => {
      history.push(getBankIntegrationPageURL(slug, 'aspsps'))
    }, 0)
  }, [history, setPendingStep, slug])

  const acknowledgeReturn = useCallback(() => {
    setPendingStep(null)
    setReturnKind(null)
  }, [setPendingStep, setReturnKind])

  return {
    justReturned,
    returnKind,
    isHandoverPending: pendingStep === BANK_STEP && isOnBankFlow,
    startBankHandover,
    acknowledgeReturn
  }
}
